// Danmaku overlay engine. Bullets are absolutely-positioned <span>s laid out into horizontal
// lanes and advanced every frame via requestAnimationFrame + transform (the React tree only owns
// the container, so high message rates don't thrash reconciliation). Lane allocation keeps a
// bullet from overtaking the one ahead of it, and a small pending queue smooths out bursts.
import { forwardRef, useEffect, useImperativeHandle, useRef } from "react";
import { AREA_FRACTION, SPEED_SECONDS, type DanmakuSettings } from "./settings.js";

export interface DanmakuItem {
  key: string;
  text: string;
  color: string;
  bold?: boolean; // streamer messages
  self?: boolean; // the viewer's own message — outlined for visibility
}

export interface DanmakuHandle {
  emit(items: DanmakuItem[]): void;
  clear(): void;
}

interface Bullet {
  el: HTMLSpanElement;
  x: number; // left edge, px from container left
  width: number;
  speed: number; // px per second
  lane: number;
}

const GAP = 28; // min horizontal gap between consecutive bullets in a lane (px)
const MAX_PENDING = 200; // burst buffer before we start dropping the oldest
const MIN_FONT = 13;
const MAX_FONT = 44;

function measurer(): CanvasRenderingContext2D | null {
  const c = document.createElement("canvas");
  return c.getContext("2d");
}

export const DanmakuLayer = forwardRef<DanmakuHandle, { settings: DanmakuSettings; paused: boolean }>(
  function DanmakuLayer({ settings, paused }, ref) {
    const containerRef = useRef<HTMLDivElement>(null);
    const ctxRef = useRef<CanvasRenderingContext2D | null>(null);
    const activeRef = useRef<Bullet[]>([]);
    const laneTailRef = useRef<(Bullet | null)[]>([]);
    const pendingRef = useRef<DanmakuItem[]>([]);
    const rafRef = useRef<number | null>(null);
    const lastTsRef = useRef<number>(0);

    // Live geometry, recomputed on resize / settings change.
    const geomRef = useRef({ width: 0, height: 0, fontSize: 22, lineHeight: 30, lanes: 1 });
    const settingsRef = useRef(settings);
    settingsRef.current = settings;
    const pausedRef = useRef(paused);
    pausedRef.current = paused;

    const recomputeGeometry = () => {
      const el = containerRef.current;
      if (!el) return;
      const width = el.clientWidth;
      const height = el.clientHeight;
      const fontSize = Math.round(
        Math.min(MAX_FONT, Math.max(MIN_FONT, height * 0.045 * settingsRef.current.fontScale)),
      );
      const lineHeight = Math.round(fontSize * 1.45);
      const usable = height * AREA_FRACTION[settingsRef.current.area];
      const lanes = Math.max(1, Math.min(14, Math.floor(usable / lineHeight)));
      geomRef.current = { width, height, fontSize, lineHeight, lanes };
      if (ctxRef.current) ctxRef.current.font = `600 ${fontSize}px Inter, system-ui, sans-serif`;
      // Shrink the lane bookkeeping if the lane count dropped.
      laneTailRef.current.length = lanes;
    };

    // Is `lane` safe to receive a new bullet of the given width/speed without a collision?
    const laneIsFree = (lane: number, width: number, speed: number): boolean => {
      const ahead = laneTailRef.current[lane];
      const W = geomRef.current.width;
      if (!ahead || !ahead.el.isConnected) return true;
      const aheadRight = ahead.x + ahead.width;
      // The bullet ahead must have fully entered, leaving a comfortable gap.
      if (aheadRight + GAP > W) return false;
      // And the new (possibly faster) bullet must not catch it before it exits the left edge.
      if (speed <= ahead.speed) return true;
      const gap = W - aheadRight; // current clearance
      const timeToClose = gap / (speed - ahead.speed);
      const aheadExitTime = aheadRight / ahead.speed;
      return timeToClose >= aheadExitTime;
    };

    const spawn = (item: DanmakuItem): boolean => {
      const el = containerRef.current;
      const ctx = ctxRef.current;
      if (!el || !ctx) return false;
      const { width: W, fontSize, lineHeight, lanes } = geomRef.current;
      const textWidth = ctx.measureText(item.text).width;
      const width = Math.ceil(textWidth) + 16;
      const distance = W + width;
      const speed = distance / SPEED_SECONDS[settingsRef.current.speed];

      // Find the lane with the most clearance among the free ones (spreads bullets out).
      let chosen = -1;
      let bestClearance = -Infinity;
      for (let lane = 0; lane < lanes; lane++) {
        if (!laneIsFree(lane, width, speed)) continue;
        const ahead = laneTailRef.current[lane];
        const clearance = ahead && ahead.el.isConnected ? W - (ahead.x + ahead.width) : Infinity;
        if (clearance > bestClearance) {
          bestClearance = clearance;
          chosen = lane;
        }
      }
      if (chosen < 0) return false;

      const span = document.createElement("span");
      span.textContent = item.text;
      span.style.cssText =
        "position:absolute;top:0;left:0;white-space:nowrap;will-change:transform;" +
        "pointer-events:none;font-weight:" +
        (item.bold ? "700" : "500") +
        ";font-size:" +
        fontSize +
        "px;line-height:" +
        lineHeight +
        "px;color:" +
        item.color +
        ";text-shadow:0 0 3px rgba(0,0,0,.85),0 1px 2px rgba(0,0,0,.9);";
      if (item.self) {
        span.style.padding = "0 6px";
        span.style.borderRadius = "6px";
        span.style.outline = "1.5px solid rgba(255,255,255,.85)";
      }
      const y = chosen * lineHeight + 2;
      span.style.transform = `translate3d(${W}px, ${y}px, 0)`;
      el.appendChild(span);

      const bullet: Bullet = { el: span, x: W, width, speed, lane: chosen };
      activeRef.current.push(bullet);
      laneTailRef.current[chosen] = bullet;
      return true;
    };

    const frame = (ts: number) => {
      rafRef.current = requestAnimationFrame(frame);
      const last = lastTsRef.current || ts;
      // Clamp dt so a backgrounded tab doesn't teleport every bullet off-screen at once.
      const dt = Math.min(0.05, (ts - last) / 1000);
      lastTsRef.current = ts;
      if (pausedRef.current || !settingsRef.current.enabled) return;

      // Flush as many pending bullets as the lanes will accept this frame (keeps order).
      const pending = pendingRef.current;
      while (pending.length) {
        if (!spawn(pending[0]!)) break;
        pending.shift();
      }

      const active = activeRef.current;
      for (let i = active.length - 1; i >= 0; i--) {
        const b = active[i]!;
        b.x -= b.speed * dt;
        if (b.x + b.width < 0) {
          b.el.remove();
          if (laneTailRef.current[b.lane] === b) laneTailRef.current[b.lane] = null;
          active.splice(i, 1);
        } else {
          b.el.style.transform = `translate3d(${b.x}px, ${b.lane * geomRef.current.lineHeight + 2}px, 0)`;
        }
      }
    };

    useImperativeHandle(ref, () => ({
      emit(items: DanmakuItem[]) {
        if (!settingsRef.current.enabled) return;
        const pending = pendingRef.current;
        pending.push(...items);
        if (pending.length > MAX_PENDING) pending.splice(0, pending.length - MAX_PENDING);
      },
      clear() {
        for (const b of activeRef.current) b.el.remove();
        activeRef.current = [];
        laneTailRef.current = [];
        pendingRef.current = [];
      },
    }));

    useEffect(() => {
      ctxRef.current = measurer();
      recomputeGeometry();
      const el = containerRef.current;
      const ro = new ResizeObserver(() => recomputeGeometry());
      if (el) ro.observe(el);
      rafRef.current = requestAnimationFrame(frame);
      return () => {
        ro.disconnect();
        if (rafRef.current != null) cancelAnimationFrame(rafRef.current);
        for (const b of activeRef.current) b.el.remove();
        activeRef.current = [];
        laneTailRef.current = [];
        pendingRef.current = [];
      };
      // eslint-disable-next-line react-hooks/exhaustive-deps
    }, []);

    // Re-measure when font size / display area changes; clear when switched off.
    useEffect(() => {
      recomputeGeometry();
      if (!settings.enabled) {
        for (const b of activeRef.current) b.el.remove();
        activeRef.current = [];
        laneTailRef.current = [];
        pendingRef.current = [];
      }
      // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [settings.enabled, settings.area, settings.fontScale, settings.speed]);

    return (
      <div
        ref={containerRef}
        aria-hidden
        className="pointer-events-none absolute inset-0 z-10 overflow-hidden"
        style={{ opacity: settings.enabled ? settings.opacity : 0, transition: "opacity .2s" }}
      />
    );
  },
);
